import React, {useCallback, useEffect, useState} from "react";
import {IncidentFeedForm} from "./incident-feed-form";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {apiV2} from "@/api/axiosConfig";
import {Client} from "@/types/userManagement";

type IncidentFeed = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    type: string;
    reference: string;
    isDefault: boolean;
}

export function EditIncidentFeed() {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prevUrl = searchParams.get("prev") ?? "/incident-feeds";
    const [feed, setFeed] = useState<IncidentFeed | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;

        apiV2.get<IncidentFeed>(`incident-feeds/${id}`)
            .then(response => {
                setFeed(response.data);
            })
            .catch(error => {
                console.error("Failed to load incident feed:", error);
                alert("Failed to load incident feed");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id]);

    const handleSubmit = useCallback(async (data: {
        clientID?: string;
        name: string;
        type: string;
        reference: string;
        isDefault: boolean;
    }) => {
        if (!id) return;

        try {
            await apiV2.patch(`incident-feeds/${id}`, data);
            navigate(prevUrl);
        } catch (error) {
            console.error("Failed to update incident feed:", error);
            alert("Failed to update incident feed");
            throw error;
        }
    }, [id, navigate]);

    const handleCancel = useCallback(() => {
        navigate(prevUrl);
    }, [navigate, prevUrl]);

    const breadcrumb = (
        <nav className="flex items-center gap-2 text-sm text-light-grey mb-4">
            <Link to="/sites" className="hover:text-grey">Sites</Link>
            <span>&gt;</span>
            <Link to={prevUrl} className="hover:text-grey">Incident Feeds</Link>
            <span>&gt;</span>
            <span className="text-grey">Edit Incident Feed</span>
        </nav>
    );

    if (loading) {
        return <div>{breadcrumb}Loading...</div>;
    }

    if (!feed) {
        return <div>{breadcrumb}Incident feed not found</div>;
    }

    return (
        <div>
            {breadcrumb}
            <IncidentFeedForm
                mode="edit"
                initialData={{
                    name: feed.name,
                    client: feed.clientID ? {
                        id: feed.clientID,
                        name: feed.clientName,
                    } as Client : undefined,
                    type: feed.type,
                    reference: feed.reference,
                    isDefault: feed.isDefault,
                }}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </div>
    );
}

